import { useAuth } from '../context/AuthContext';
import { useTrialStatus } from './useTrialStatus';
import { type AppPlan, type FeatureKey, FEATURE_PLAN_MAP, planMeets } from '../lib/planLimits';

/**
 * Derives the current user's access plan from their trial status
 * and Stripe subscription, then exposes a `can(feature)` helper.
 *
 * Plan resolution:
 *  - loading              → 'free' (safe default while resolving)
 *  - active paid sub      → plan name from subscription ('vital' | 'lifetime')
 *  - free trial (active / warning) → 'lifetime' (full unlock during trial)
 *  - expired / grace      → 'free'
 */
export function useFeatureAccess() {
  // Reuse the single Firestore listener from useTrialStatus — no duplicate subscription
  const { phase, hasActiveSubscription, subscription } = useTrialStatus();

  const plan: AppPlan = (() => {
    // Still loading auth / subscription data — hold at free
    if (phase === 'loading') return 'free';

    // Paid subscription is active → use the normalised planId if available,
    // falling back to planName string matching
    if (hasActiveSubscription) {
      const id = (subscription?.planId ?? '').toLowerCase();
      if (id === 'lifetime') return 'lifetime';
      if (id === 'vital') return 'vital';
      const name = (subscription?.planName ?? '').toLowerCase();
      if (name.includes('lifetime') || name.includes('expert companion')) return 'lifetime';
      if (name.includes('vital')) return 'vital';
      // Active sub with unknown plan — give vital at minimum
      if (subscription) return 'vital';
    }

    // No paid sub but within the free trial window → full access
    if (phase === 'active' || phase === 'warning') return 'lifetime';

    // Expired trial / grace period / no sub
    return 'free';
  })();

  /** Returns true when the current plan can access the given feature. */
  function can(feature: FeatureKey): boolean {
    return planMeets(plan, FEATURE_PLAN_MAP[feature] as AppPlan);
  }

  return { plan, can };
}
